import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  CanActivate,
  GuardsCheckEnd,
  PRIMARY_OUTLET,
  Router,
  RunGuardsAndResolvers,
  UrlTree,
} from '@angular/router';
import { AuthRedirectService, AuthRedirectStorageService, AuthService } from '@spartacus/core';
import { Observable, from, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CustomLogoutGuard implements CanActivate {
  constructor(
    private auth: AuthService,
    private router: Router,
    private authRedirectService: AuthRedirectService,
    private authRedirectStorageService: AuthRedirectStorageService,
    private activatedRoute: ActivatedRoute,
  ) {}

  canActivate(): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    this.authRedirectService.saveCurrentNavigationUrl();

    const primaryRoute: ActivatedRoute = this.getLastRouteForOutlet(this.activatedRoute.root, PRIMARY_OUTLET);
    /**
     * меняем стратегии поведения роута при редиректе на активную страницу
     * так по умолчанию, если предыдущий роут и текущий роут совпадают, то роутер не выполняет Guards и Resolvers
     */
    const restoreRunGuards = this.changeRunGuardStrategy(primaryRoute, 'always');

    // нужно для сброса исходных параметров роутера
    this.router.events
      .pipe(
        filter((e) => e instanceof GuardsCheckEnd),
        take(1),
      )
      .subscribe(() => {
        restoreRunGuards();
      });

    return from(this.logout()).pipe(
      switchMap(() => this.authRedirectStorageService.getRedirectUrl()),
      switchMap((url) => this.forceNavigation(url)),
    );
  }

  protected logout(): Promise<any> {
    return this.auth.coreLogout();
  }

  /**
   * Так как после логаута мы переходим на ту же страницу, что и были, то маршрутизатор не считает это за новый маршрут
   * при стратегии 'ignore' и не перезагружает компоненты. Для этого добавляем параметр в URL
   */
  private forceNavigation(url = '/'): Observable<boolean | UrlTree> {
    // Добавляем параметр, чтобы маршрутизатор воспринимал URL как новый
    const separator = url.includes('?') ? '&' : '?';
    const urlWithParam = `${url}${separator}logout=yes`;

    return from(
      this.router.navigateByUrl(urlWithParam, {
        // Используем skipLocationChange && replaceUrl, чтобы не сохранять временный параметр в истории браузера
        skipLocationChange: true,
        replaceUrl: true,
      }),
    ).pipe(
      switchMap(() => {
        // После навигации чтобы удалить временный параметр из URL переходим на тот же URL без него
        return of(this.router.parseUrl(url));
      }),
    );
  }

  private changeRunGuardStrategy(route: ActivatedRoute, strategy: RunGuardsAndResolvers): () => void {
    const routeConfigs = route.pathFromRoot.map((item) => {
      if (item.routeConfig) {
        const runGuardsAndResolvers = item.routeConfig.runGuardsAndResolvers;
        item.routeConfig.runGuardsAndResolvers = strategy;

        return runGuardsAndResolvers;
      } else {
        return undefined;
      }
    });

    return () => {
      route.pathFromRoot.forEach((item, index) => {
        if (item.routeConfig) {
          item.routeConfig.runGuardsAndResolvers = routeConfigs[index];
        }
      });
    };
  }

  private getLastRouteForOutlet(route: ActivatedRoute, outlet: string): ActivatedRoute {
    if (route.children?.length) {
      return this.getLastRouteForOutlet(
        route.children.find((item) => item.outlet === outlet),
        outlet,
      );
    } else {
      return route;
    }
  }
}
